import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDate } from '../lib/util';
import { StatusBadge } from '../components/StatusBadge';
import { DeadlineIndicator } from '../components/DeadlineIndicator';
import { getCurrentUser, isAssignedAnalyst } from '../auth/useUser';
import { PagingControls } from '../components/PagingControls';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { useAppData } from '../state/contexts/AppDataContext';
import { useLifecycleActions } from '../state/actions/lifecycle';
import { unionMatching, useCaseQuery } from '../state/useCaseQuery';
import { finalChangedHold } from './CaseDetail/tabs/DecisionTab';

export function CureAppeals() {
  const { cases, now } = useAppData();
  const { closeCureWindow, receiveAppeal, recordCure, issueFinalDetermination } = useLifecycleActions();
  const user = getCurrentUser()!;
  const canApprove = user.canApprove;
  const youRecommended = (c: { recommendedBy?: string | null }) =>
    !!c.recommendedBy && !!user.serverId && c.recommendedBy === user.serverId;

  // H-2: cure windows, appeals, and the final-determination history all
  // query the full visible population server-side; the store union keeps
  // local transitions (closeCureWindow / receiveAppeal / recordCure /
  // issueFinalDetermination) moving rows between sections immediately.
  const cureQuery = useCaseQuery({ cureStatus: 'OPEN' });
  const appealQuery = useCaseQuery({ status: 'APPEAL' });
  const finalsQuery = useCaseQuery({ hasFinalDetermination: true });

  const cureOpen = unionMatching(cureQuery.cases, cases, c => c.cureStatus === 'OPEN');
  const appeals = unionMatching(appealQuery.cases, cases, c => c.status === 'APPEAL');
  const finals = unionMatching(finalsQuery.cases, cases, c => Boolean(c.finalDetermination), finalsQuery.offset);

  // Client-side v1 search + stage facet over the in-memory rows the per-section
  // server queries already returned (server-side filtering is a deferred phase
  // 2). One bar drives all three sections: rows are tagged with their stage so
  // a single query/facet filters cure windows, appeals, and finals together.
  const rows = [
    ...cureOpen.map(c => ({ c, stage: 'cure' as const })),
    ...appeals.map(c => ({ c, stage: 'appeal' as const })),
    ...finals.map(c => ({ c, stage: 'final' as const })),
  ];
  const { filtered: filteredRows, bar } = useSearchFilter(
    rows,
    ({ c }) => [c.id, c.registrant, ...(c.reasonCodes ?? []), c.finalDetermination].filter(Boolean).join(' '),
    [
      {
        id: 'stage',
        label: 'Stage',
        options: [
          { value: '', label: 'All stages' },
          { value: 'cure', label: 'Cure window' },
          { value: 'appeal', label: 'Appeal' },
          { value: 'final', label: 'Final determination' },
        ],
        predicate: ({ stage }, v) => stage === v,
      },
    ],
    { placeholder: 'Search by case, registrant, or reason code…' },
  );
  const filteredCure = filteredRows.filter(r => r.stage === 'cure').map(r => r.c);
  const filteredAppeals = filteredRows.filter(r => r.stage === 'appeal').map(r => r.c);
  const filteredFinals = filteredRows.filter(r => r.stage === 'final').map(r => r.c);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>Cure Windows & Appeals</h2>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>12 business day cure window · 90 calendar day appeal reassessment</div>
      {(cureOpen.length > 0 || appeals.length > 0 || finals.length > 0) && <SearchFilterBar {...bar} />}
      {filteredCure.length > 0 && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.warn, marginBottom: 12 }}>Open Cure Windows ({Math.max(cureQuery.total, cureOpen.length)})</h3>
          {filteredCure.map(c => (
            <div key={c.id} style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <strong style={{ color: C.goldText }}>{c.id}</strong> — <strong>{c.registrant}</strong>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Unsuccessful notice sent: {fmtDate(c.notificationSentAt)}</div>
                  <DeadlineIndicator deadline={c.cureDeadline} label="12 biz day cure deadline" now={now} />
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Reason(s): {c.reasonCodes.join('; ')}</div>
                </div>
                {/* #587 — TWO BUTTONS, because they are two events.
                    This was one button, "Record Appeal / Cure Info", and both
                    halves of its name called `receiveAppeal`. So a registrant
                    who sent the missing document and one who disputed the
                    finding produced the same record — same cureStatus, same
                    APPEAL_RECEIVED audit code, same 90-day appeal clock — and
                    an analyst reading the case later could not tell which had
                    happened. Splitting the control is the whole fix: the
                    discriminator is which button an approver presses, and
                    there is no default, because there is no combined control
                    left to press by accident.
                    Cure first: it is the outcome the cure window exists to
                    produce, and the destructive one (Close) stays last and
                    outlined. */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {canApprove && <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => recordCure(c.id)}>Record Cure</button>}
                  {canApprove && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => receiveAppeal(c.id)}>Record Appeal</button>}
                  {canApprove && <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => closeCureWindow(c.id)}>Close (No Response)</button>}
                  {!canApprove && <span style={{ fontSize: 12, color: C.textMuted }}>Approver action required</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {filteredAppeals.length > 0 && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.special}` }}>
          {/* #399 — was the literal #805AD5, which is 3.50:1 on the dark
              surface. C.special is the StatusBadge Final-Determination purple,
              pinned at AA against both palettes by theme.contrast.test.ts. */}
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.special, marginBottom: 12 }}>Active Appeals ({Math.max(appealQuery.total, appeals.length)})</h3>
          {filteredAppeals.map(c => (
            <div key={c.id} style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <strong style={{ color: C.goldText }}>{c.id}</strong> — <strong>{c.registrant}</strong>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Appeal received: {fmtDate(c.appealReceivedAt)}</div>
                  <DeadlineIndicator deadline={c.appealDeadline} label="90-day reassessment deadline" now={now} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* Same dual-control guard as the Overview tab's Issue Final
                      buttons: the OA/VA Agent must not issue the final
                      determination (REMAINING_GAPS clarification #2, D4). */}
                  {/* #781 review — nor the approver who RECOMMENDED the case
                      (reassigned away from them since): the API refuses both
                      determinations as approver_is_not_recommender, and the
                      breach row pages on-call. serverId space (CLAUDE.md B-2). */}
                  {canApprove && !isAssignedAnalyst(c.analystId, user) && !youRecommended(c) ? <>
                    {/* #781 — Final: Changed is a decision, and the API refuses
                        one on a case with no current recommendation; the agent
                        makes it on the case's Decision tab. Final: Unchanged
                        needs none. #797 — the decision under appeal consumed the
                        one it was taken on, so this is a NEW one; and the KYC
                        minimums hold it too (#772), as on the Overview tab. */}
                    {(() => {
                      const hold = finalChangedHold(c);
                      if (hold?.reason === 'recommendation') {
                        return <span style={{ fontSize: 12, color: C.textMuted, alignSelf: 'center' }} data-testid="final-changed-needs-recommendation">Final: Changed needs a new recommendation from the OA/VA Agent, made since the decision</span>;
                      }
                      const holdId = `final-changed-kyc-hold-${c.id}`;
                      return (
                        <>
                          <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall, ...(hold ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }} disabled={!!hold} aria-describedby={hold ? holdId : undefined} onClick={() => issueFinalDetermination(c.id, 'CHANGED')}>Final: Changed (Approve)</button>
                          {hold && (
                            <span id={holdId} style={{ fontSize: 12, color: C.warn, alignSelf: 'center' }} data-testid="final-changed-kyc-hold">
                              Held until every required KYC check meets its evidence minimum. Still short: {hold.unmet.map(u => `${u.label} (${u.progress})`).join('; ')}
                            </span>
                          )}
                        </>
                      );
                    })()}
                    <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => issueFinalDetermination(c.id, 'UNCHANGED')}>Final: Unchanged</button>
                  </> : <span style={{ fontSize: 12, color: C.textMuted }}>
                    {!canApprove ? 'Approver action required'
                      : isAssignedAnalyst(c.analystId, user)
                        ? 'You are the OA/VA Agent on this case — a different approver must issue the final determination'
                        : 'You recommended this case — a different approver must issue the final determination'}
                  </span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {filteredFinals.length > 0 && (
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.textMuted, marginBottom: 12 }}>Final Determinations ({Math.max(finalsQuery.total, finals.length)})</h3>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Case</th><th style={styles.th}>Registrant</th><th style={styles.th}>Outcome</th><th style={styles.th}>Date</th><th style={styles.th}>Note</th>
            </tr></thead>
            <tbody>
              {filteredFinals.map(c => (
                <tr key={c.id}>
                  <td style={styles.td}><span style={{ color: C.goldText, fontWeight: 600 }}>{c.id}</span></td>
                  <td style={styles.td}>{c.registrant}</td>
                  <td style={styles.td}><StatusBadge status={c.finalDetermination!} /></td>
                  <td style={styles.td}>{fmtDate(c.finalDeterminationDate)}</td>
                  <td style={styles.td}><span style={{ fontSize: 12, color: C.textMuted }}>No further appeals permitted</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <PagingControls offset={finalsQuery.offset} limit={finalsQuery.limit} total={finalsQuery.total} count={finals.length} onOffset={finalsQuery.setOffset} label="final determinations" />
        </div>
      )}
      {cureOpen.length === 0 && appeals.length === 0 && finals.length === 0 && (
        <div style={{ ...styles.card, textAlign: 'center', color: C.textMuted, padding: 40 }}>No active cure windows, appeals, or final determinations</div>
      )}
      {(cureOpen.length > 0 || appeals.length > 0 || finals.length > 0) && filteredRows.length === 0 && (
        <div style={{ ...styles.card, textAlign: 'center', color: C.textMuted, padding: 40 }}>Nothing matches the current filters</div>
      )}
    </div>
  );
}
